import { useLayoutEffect, useRef } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { acquireOverlay } from "@/shared/lib/backBridge";
import { OVERLAY_DIM_MS } from "@/shared/lib/motion";
import { useBodyScrollLock } from "@/shared/hooks/useBodyScrollLock";
import { useOverlayPresence } from "@/shared/hooks/useOverlayPresence";
import useLoadingStore from "@/shared/model/loadingStore";
import loadingLottie from "@/assets/lottie/loading.json?url";
import styles from "./LoadingBox.module.scss";

const LoadingBox = () => {
  const isLoading = useLoadingStore((s) => s.isLoading);
  const message = useLoadingStore((s) => s.message);
  const overlayRef = useRef<HTMLDivElement>(null);
  // 240ms 페이드 인·아웃(다른 Dim 과 같은 --overlay-dim-duration·표준 이징, B2 결정 2026-10-27).
  // 켜는 커밋에 바로 그려 뜨는 순간부터 탭을 막고, 끄면 사라지는 동안은 탭을 막지 않는다(아래 .leaving).
  const { shouldRender, isAnimating } = useOverlayPresence(isLoading, OVERLAY_DIM_MS, overlayRef);

  // 로딩 중 네이티브 back 무시 — 요청은 취소할 수 없으므로 이탈 자체를 막는다.
  // 등록하지 않으면 back 이 5단계 계약의 ①(overlay)를 건너뛰고 ②(스텝 슬롯)로 새어
  // 폼이 한 스텝 되감기고, 취소되지 않은 요청의 늦은 응답이 되감긴 화면에 작용한다.
  // PaymentConfirmLoading 과 동일한 no-op close 형태 (닫을 수 있는 오버레이가 아니라 봉쇄용).
  // 등록은 켜져 있는 동안만 — 사라지는 페이드 동안은 뒤로가기도 막지 않는다.
  // layout effect: 덮개를 그린 커밋에서 바로 등록한다(공용 useOverlayBackRegistration 과 같은 시점).
  // ⛔ 조기 return 위에 둘 것 (Rules of Hooks).
  useLayoutEffect(() => {
    if (!isLoading) return;
    return acquireOverlay(() => {});
  }, [isLoading]);

  // 뒤 화면 잠금(공용 잠금 — B2 결정 2026-10-27) — 뒤로가기 막기와 같은 축: 켜져 있는 동안만, 사라지는 페이드 동안은 해제.
  useBodyScrollLock(isLoading);

  if (!shouldRender) return null;

  return (
    <div
      ref={overlayRef}
      className={`${styles.loadingOverlay} ${isAnimating ? styles.visible : ""} ${!isLoading ? styles.leaving : ""}`}
      role="status"
      aria-live="polite"
    >
      {/* 로더는 장식 — 래퍼가 고정 크기(80px), 플레이어가 채움 (ExamCompleteStep 동일 구조) */}
      <div className={styles.loader} aria-hidden="true">
        <DotLottieReact src={loadingLottie} autoplay loop className={styles.loaderPlayer} />
      </div>
      {/* 시각 미표시 — 스크린리더 전용 상태 텍스트 (message 없으면 기본값).
          사라지는 동안은 비운다 — 끌 때 message 가 지워져 문구가 '로딩 중'으로 바뀌며 다시 읽히지 않게. */}
      {isLoading && <span className={styles.srOnly}>{message ?? "로딩 중"}</span>}
    </div>
  );
};

export default LoadingBox;
